import {
  DIGIRASA_AUTH_UPDATED_EVENT,
  DIGIRASA_SESSION_EXPIRED_EVENT,
  getAccessTokenExpiryMs,
  readStoredAuth,
} from '../pages/Account/profileFormUtils';

/** Fired after the user successfully extends the session (new access token saved). */
export const DIGIRASA_SESSION_EXTENDED_EVENT = 'digirasa-session-extended';

let promptActive = false;
let expiryTimerId = null;

export function isSessionExpiryPromptActive() {
  return promptActive;
}

/**
 * Ask the user to extend or log out. Keeps stored user details until they choose.
 * @param {{ reason?: string }} [detail]
 * @returns {boolean} whether a prompt is (or was already) active
 */
export function promptSessionExpired(detail = {}) {
  const { token } = readStoredAuth();
  if (!token) return false;
  if (promptActive) return true;
  promptActive = true;
  clearSessionExpiryWatch();
  window.dispatchEvent(
    new CustomEvent(DIGIRASA_SESSION_EXPIRED_EVENT, {
      detail: { reason: detail.reason || 'expired' },
    }),
  );
  return true;
}

/** Keep promptActive in sync when 401 handlers fire the event directly. */
function onSessionExpiredEvent() {
  promptActive = true;
  clearSessionExpiryWatch();
}

export function markSessionExpiryPromptClosed() {
  promptActive = false;
}

export function notifySessionExtended() {
  window.dispatchEvent(new Event(DIGIRASA_SESSION_EXTENDED_EVENT));
}

export function clearSessionExpiryWatch() {
  if (expiryTimerId != null) {
    window.clearTimeout(expiryTimerId);
    expiryTimerId = null;
  }
}

/**
 * Schedule a prompt when the access JWT expires. Call after login / refresh / auth sync.
 */
export function scheduleSessionExpiryWatch() {
  clearSessionExpiryWatch();
  if (promptActive) return;

  const { token } = readStoredAuth();
  if (!token) return;

  const expMs = getAccessTokenExpiryMs(token);
  if (expMs == null) return;

  const delay = expMs - Date.now();
  if (delay <= 0) {
    promptSessionExpired({ reason: 'expired' });
    return;
  }

  // Cap setTimeout to avoid 32-bit overflow for very long-lived tokens.
  const capped = Math.min(delay, 2_147_483_647);
  expiryTimerId = window.setTimeout(() => {
    expiryTimerId = null;
    promptSessionExpired({ reason: 'expired' });
  }, capped);
}

/** Start watching JWT expiry and re-schedule whenever auth storage changes. */
export function startSessionExpiryWatcher() {
  scheduleSessionExpiryWatch();
  const onAuth = () => scheduleSessionExpiryWatch();
  window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
  window.addEventListener(DIGIRASA_SESSION_EXPIRED_EVENT, onSessionExpiredEvent);
  return () => {
    window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
    window.removeEventListener(DIGIRASA_SESSION_EXPIRED_EVENT, onSessionExpiredEvent);
    clearSessionExpiryWatch();
  };
}
